import React from "react";
import { Star, MoreHorizontal, Calendar } from "lucide-react";
import { Project, User } from "../../types";

interface JiraProjectGridProps {
  projects: Project[];
  users: User[];
  onProjectClick: (project: Project) => void;
}

/* ----------------------------- Helpers ----------------------------- */

const stringToColor = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 70%, 45%)`;
};

const getInitial = (name?: string, email?: string) => {
  const s = (name || email || "").trim();
  const m = s.match(/\p{L}|\p{N}/u);
  return m ? m[0].toUpperCase() : "?";
};

export const Avatar: React.FC<{
  src?: string | null;
  name?: string;
  email?: string;
  className?: string;
  size?: number; // px
  title?: string;
}> = ({ src, name, email, className = "", size = 32, title }) => {
  const [error, setError] = React.useState(false);
  const label = name || email || "";
  const bg = stringToColor(label);
  const styleSize = { width: size, height: size };

  if (src && src.trim() !== "" && !error) {
    return (
      <img
        src={src}
        alt={label}
        title={title || label}
        onError={() => setError(true)}
        className={`rounded-full border-2 border-white shadow-sm ${className}`}
        style={styleSize}
      />
    );
  }

  return (
    <div
      className={`rounded-full border-2 border-white shadow-sm flex items-center justify-center text-white text-[0.7rem] font-semibold ${className}`}
      style={{ ...styleSize, backgroundColor: bg }}
      aria-label={label}
      title={title || label}
    >
      {getInitial(name, email)}
    </div>
  );
};

const ProjectIcon: React.FC<{ url?: string; fallbackText?: string }> = ({ url, fallbackText }) => {
  const [err, setErr] = React.useState(false);

  let favicon = "";
  try {
    if (url) favicon = `https://www.google.com/s2/favicons?domain=${new URL(url).hostname}&sz=32`;
  } catch {
    // ignore invalid URL
  }

  if (favicon && !err) {
    return (
      <img
        src={favicon}
        alt="Project icon"
        className="w-8 h-8 rounded-md object-cover"
        onError={() => setErr(true)}
      />
    );
  }

  return <span className="text-xl text-white">{fallbackText || "📁"}</span>;
};

/* --------------------------- Main Component --------------------------- */

export const JiraProjectGrid: React.FC<JiraProjectGridProps> = ({
  projects,
  users,
  onProjectClick,
}) => {
  // Normalize empty avatar urls once
  const usersSafe = React.useMemo(
    () => users.map((u) => ({ ...u, avatarUrl: u.avatarUrl || undefined })),
    [users]
  );

  const getProjectLead = (leadId: string) => usersSafe.find((user) => user.id === leadId);

  const getProjectTypeColor = (type: string) => {
    switch (type) {
      case "software":
        return "bg-blue-100 text-blue-800";
      case "business":
        return "bg-green-100 text-green-800";
      case "service-desk":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Projects</h2>
        <p className="text-gray-600">Click a project to open its Kanban board</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project) => {
          const lead = getProjectLead(project.lead);
          const members =
            (project.teamMembers || [])
              .map((id) => usersSafe.find((u) => u.id === id))
              .filter((u): u is User => Boolean(u)) || [];

          return (
            <div
              key={project.id}
              onClick={() => onProjectClick(project)}
              className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg hover:border-blue-300 transition-all duration-200 cursor-pointer transform hover:-translate-y-1"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center text-xl shadow-md relative overflow-hidden">
                    <ProjectIcon url={project.url} fallbackText={project.avatar} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{project.name}</h3>
                    <p className="text-sm text-gray-500">{project.key}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                    Kanban Board
                  </span>
                  <button
                    type="button"
                    className="p-1 text-gray-400 hover:text-yellow-500 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Star project"
                  >
                    <Star className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                    aria-label="More options"
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-4 line-clamp-2">{project.description}</p>

              <div className="flex items-center justify-between mb-4">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getProjectTypeColor(
                    project.type
                  )}`}
                >
                  {project.type.replace("-", " ")}
                </span>
                <span className="text-xs text-gray-500">{project.category}</span>
              </div>

              {members.length > 0 && (
                <div className="flex items-center mb-4 -space-x-2">
                  {members.slice(0, 4).map((member) => (
                    <Avatar
                      key={member.id}
                      src={member.avatarUrl || undefined}
                      name={member.displayName}
                      email={member.emailAddress}
                      className="border-2 border-white"
                      size={32}
                      title={member.displayName}
                    />
                  ))}
                  {members.length > 4 && (
                    <div className="w-8 h-8 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-xs font-medium text-gray-600">
                      +{members.length - 4}
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between text-sm text-gray-500">
                <div className="flex items-center space-x-1">
                  <Calendar className="w-4 h-4" />
                  <span>
                    {project.endDate
                      ? `Due ${new Date(project.endDate).toLocaleDateString()}`
                      : `Updated ${new Date(project.updatedAt).toLocaleDateString()}`}
                  </span>
                </div>
                {lead && (
                  <div className="flex items-center space-x-1">
                    <Avatar
                      src={lead.avatarUrl || undefined}
                      name={lead.displayName}
                      email={lead.emailAddress}
                      size={20}
                      className="border-0"
                      title={lead.displayName}
                    />
                    <span>{lead.displayName?.split(" ")[0] || "N/A"}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
